import { useMemo, useState } from 'react';
import {
  IAssessmentResource,
  ILineResource,
  IMaintenanceResource,
} from '../../entities/farms.entities';
import { IBioSecurity, ITaskResource } from '../../entities/task.entities';
import AssessmentCell from './AssessmentCell';
import HarvestCell from './HarvestCell';
import InventorySeedCell from './InventorySeedCell';
import MaintenanceCell from './MaintenanceCell';
import RequireAssessmentCell from './RequireAssessmentCell';
import SeedingCell from './SeedingCell';
import TaskCell from './TaskCell';
import WaterQualityCell from './WaterQualityCell';
import {
  IHarvestResource,
  ISeedingResource,
} from '../../entities/growing.entities';
import { useSelector } from 'react-redux';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import { IBoatRefuel } from '../../entities/boat.entities';
import moment from 'moment';
import { toMillisecond } from '../../lib/common.helpers';
import { IInventoryManage } from '../../entities/inventory.entities';

interface RefuelProps {
  lang: any;
  data: IBoatRefuel;
}

const RefuelCell = ({ lang, data }: RefuelProps) => (
  <div className='--entity --refuel'>
    <div className='--header'>
      <span>{translate(lang, 'refuel')}</span>
    </div>
    <div className='--title'>
      <div className='mb-7'>{data.boat.name}</div>
      {data.fuel_price !== null ? (
        <div>{`${data.fuel_amount}L / $${data.fuel_price}`}</div>
      ) : (
        <div>{`${data.fuel_amount}L`}</div>
      )}
      <div className='mt-7'>
        {`${moment(toMillisecond(data.start_time)).format(
          'hh:mm A',
        )} - ${moment(toMillisecond(data.finish_time)).format('hh:mm A')}`}
      </div>
      {!data.location !== null && <div className='mt-7'>{data.location}</div>}
    </div>
  </div>
);

interface ICell {
  seedings: ISeedingResource[];
  assessments: IAssessmentResource[];
  maintenances: IMaintenanceResource[];
  inventory_seeds: IInventoryManage[];
  harvests: IHarvestResource[];
  tasks: ITaskResource[];
  req_assess_seeds: ILineResource[];
  boat_refuels: IBoatRefuel[];
  water_qualities: IBioSecurity[];
}

interface Props {
  cellData: ICell;
  onShowTask: (data: ITaskResource) => void;
  onCompleteTask: (data: ITaskResource) => void;
  onEditTask: (data: ITaskResource) => void;
  onDeleteTask: (data: ITaskResource) => void;
  onShowSeed: (data: any) => void;
  onShowAssessment: (d: any) => void;
  onShowMaintenance: (d: any) => void;
  onShowFloats: (d: any) => void;
  onShowReqAssess: (d: any) => void;
  onShowHarvest: (d: any) => void;
  onShowBioSecurity: (d: IBioSecurity) => void;
  perPage?: number;
}

const CalendarCell = ({
  cellData,
  onShowTask,
  onCompleteTask,
  onEditTask,
  onDeleteTask,
  onShowSeed,
  onShowAssessment,
  onShowMaintenance,
  onShowFloats,
  onShowReqAssess,
  onShowHarvest,
  onShowBioSecurity,
  perPage = 5,
}: Props) => {
  const lang = useSelector(selectLang);

  const entities: Array<{ type: string; data: any }> = [
    ...cellData.req_assess_seeds.map(x => ({ type: 'REQ', data: x })),
    ...cellData.tasks.map(x => ({ type: 'TASK', data: x })),
    ...cellData.seedings.map(x => ({ type: 'SEED', data: x })),
    ...cellData.assessments.map(x => ({ type: 'ASS', data: x })),
    ...cellData.maintenances.map(x => ({ type: 'MNT', data: x })),
    ...cellData.inventory_seeds.map(x => ({ type: 'IVT', data: x })),
    ...cellData.harvests.map(x => ({ type: 'HAR', data: x })),
    ...cellData.boat_refuels.map(x => ({ type: 'REFUEL', data: x })),
    ...(cellData.water_qualities ?? []).map(x => ({ type: 'WQ', data: x })),
  ];

  const pageSize = useMemo(
    () => Math.ceil(entities.length / perPage),
    [entities],
  );
  const [curPage, setCurPage] = useState(1);

  const handlePageChange = (btn: 'next' | 'prev') => {
    if (btn === 'next' && curPage < pageSize) {
      setCurPage(curPage + 1);
    } else if (btn === 'prev' && curPage > 1) {
      setCurPage(curPage - 1);
    }
  };
  const entityData = useMemo(
    () =>
      entities.filter(
        (x, i) => i >= (curPage - 1) * perPage && i < curPage * perPage,
      ),
    [perPage, curPage, entities],
  );

  return (
    <div className='cell-wrap'>
      <div className='--entities'>
        {entityData.map((item, idx) =>
          item.type === 'TASK' ? (
            <TaskCell
              key={idx}
              task={item.data}
              onShowTask={onShowTask}
              onCompleteTask={onCompleteTask}
              onEditTask={onEditTask}
              onDeleteTask={onDeleteTask}
            />
          ) : item.type === 'SEED' ? (
            <SeedingCell
              key={idx}
              seedData={item.data}
              onShowSeed={onShowSeed}
            />
          ) : item.type === 'ASS' ? (
            <AssessmentCell
              key={idx}
              assessData={item.data}
              onShowAssessment={onShowAssessment}
            />
          ) : item.type === 'MNT' ? (
            <MaintenanceCell
              key={idx}
              mntData={item.data}
              onShowMaintenance={onShowMaintenance}
            />
          ) : item.type === 'IVT' ? (
            <InventorySeedCell
              key={idx}
              floatsData={item.data}
              onShowFloats={onShowFloats}
            />
          ) : item.type === 'HAR' ? (
            <HarvestCell
              key={idx}
              harvestData={item.data}
              onClickCell={onShowHarvest}
            />
          ) : item.type === 'REQ' ? (
            <RequireAssessmentCell
              key={idx}
              reqLine={item.data}
              onClickAssessment={onShowReqAssess}
            />
          ) : item.type === 'REFUEL' ? (
            <RefuelCell key={idx} data={item.data} lang={lang} />
          ) : item.type === 'WQ' ? (
            <WaterQualityCell
              key={idx}
              bioData={item.data}
              onShowBioSecurity={onShowBioSecurity}
            />
          ) : (
            <></>
          ),
        )}
      </div>
      <div className='--buttons'>
        {entities.length > perPage && (
          <div className='paginator'>
            {curPage > 1 && (
              <button
                className='prev-btn'
                onClick={handlePageChange.bind(this, 'prev')}
              >
                <span>{translate(lang, 'Previous')}</span>
              </button>
            )}
            <div className='page-num'>
              {curPage}/{pageSize}
            </div>
            {curPage < pageSize && (
              <button
                className='prev-btn'
                onClick={handlePageChange.bind(this, 'next')}
              >
                <span>{translate(lang, 'Next')}</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarCell;

export const FarmDiv = ({
  farm,
}: {
  farm: { name: string; farm_number: string };
}) => (
  <div
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 10px',
      backgroundColor: '#e3f2fd',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: 500,
      color: '#1565c0',
      border: '1px solid #90caf9',
    }}
  >
    <span style={{ marginRight: '4px' }}>🏭</span>
    {`${farm.name} - ${farm.farm_number}`}
  </div>
);

export const LineDiv = ({ line }: { line: { line_name: string } }) => (
  <div
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 10px',
      backgroundColor: '#f3e5f5',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: 500,
      color: '#6a1b9a',
      border: '1px solid #ce93d8',
    }}
  >
    <span style={{ marginRight: '4px' }}>📍</span>
    {line.line_name}
  </div>
);
